import { useState, useEffect, useRef } from "react";
import { PageLayout } from "../components/Layout";
import { useAuth, API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { 
  User, 
  Save,
  Mail,
  Linkedin,
  Globe,
  Plus,
  X,
  Camera
} from "lucide-react";
import { ProfileBadge, PROFILE_TYPE_OPTIONS } from "../components/ProfileBadge";
import BadgesAndCerts from "../components/BadgesAndCerts";
import MfaSettingsCard from "../components/MfaSettingsCard";
import WorkforceBoardCard from "../components/WorkforceBoardCard";
import InstructorZoomCard from "../components/InstructorZoomCard";

const Profile = () => {
  const { user, checkAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [photoUploading, setPhotoUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: "",
    bio: "",
    skills: [],
    interests: [],
    linkedin_url: "",
    portfolio_url: ""
  });
  const [newSkill, setNewSkill] = useState("");
  const [newInterest, setNewInterest] = useState("");
  const [profileType, setProfileType] = useState(null);
  const [profileTypeLoading, setProfileTypeLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || "",
        bio: user.bio || "",
        skills: user.skills || [],
        interests: user.interests || [],
        linkedin_url: user.linkedin_url || "",
        portfolio_url: user.portfolio_url || ""
      });
    }
  }, [user]);

  // Load current profile type
  useEffect(() => {
    const loadProfileType = async () => {
      try {
        const res = await fetch(`${API}/public/skills-matrix`);
        if (res.ok) {
          const data = await res.json();
          const myProfile = data.profiles.find(p => p.profile_id === user?.user_id);
          if (myProfile?.profile_type) setProfileType(myProfile.profile_type);
        }
      } catch {}
    };
    if (user) loadProfileType();
  }, [user]);

  const handleProfileTypeChange = async (type) => {
    setProfileTypeLoading(true);
    try {
      const res = await fetch(`${API}/talent/profile-type?profile_type=${type}`, {
        method: "PUT",
        credentials: "include",
      });
      if (res.ok) {
        setProfileType(type);
        toast.success("Profile type updated!");
      } else {
        toast.error("Failed to update profile type");
      }
    } catch {
      toast.error("An error occurred");
    } finally {
      setProfileTypeLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const addSkill = () => {
    if (newSkill.trim() && !formData.skills.includes(newSkill.trim())) {
      setFormData(prev => ({
        ...prev,
        skills: [...prev.skills, newSkill.trim()]
      }));
      setNewSkill("");
    }
  };

  const removeSkill = (skill) => {
    setFormData(prev => ({
      ...prev,
      skills: prev.skills.filter(s => s !== skill)
    }));
  };

  const addInterest = () => {
    if (newInterest.trim() && !formData.interests.includes(newInterest.trim())) {
      setFormData(prev => ({
        ...prev,
        interests: [...prev.interests, newInterest.trim()]
      }));
      setNewInterest("");
    }
  };

  const removeInterest = (interest) => {
    setFormData(prev => ({
      ...prev,
      interests: prev.interests.filter(i => i !== interest)
    }));
  };

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowed.includes(file.type)) {
      toast.error("Only JPG, PNG, or WEBP images are supported");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image too large (max 5MB)");
      return;
    }

    setPhotoUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/users/me/photo`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      if (res.ok) {
        toast.success("Profile photo updated!");
        await checkAuth();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Failed to upload photo");
      }
    } catch {
      toast.error("An error occurred uploading photo");
    } finally {
      setPhotoUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API}/users/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success("Profile updated successfully!");
        checkAuth();
      } else {
        toast.error("Failed to update profile");
      }
    } catch (error) {
      toast.error("An error occurred");
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageLayout 
      title="Profile"
      subtitle="Manage your personal information and showcase your skills."
    >
      <div className="max-w-3xl mx-auto space-y-8" data-testid="profile-page">
        {/* Profile Header */}
        <Card className="card-surface">
          <CardContent className="pt-6">
            <div className="flex items-center gap-6">
              <div className="relative group">
                <Avatar className="h-24 w-24 border-4 border-[#FFD507]/30">
                  <AvatarImage src={user?.picture} alt={user?.name} />
                  <AvatarFallback className="bg-[#1E3A8A] text-slate-900 text-3xl">
                    {user?.name?.charAt(0)?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={photoUploading}
                  className="absolute inset-0 flex items-center justify-center rounded-full bg-black/50 opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity disabled:cursor-wait"
                  data-testid="upload-photo-btn"
                  aria-label="Change profile photo"
                >
                  {photoUploading ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Camera className="w-6 h-6 text-white" />
                  )}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={handlePhotoSelect}
                  data-testid="upload-photo-input"
                />
              </div>
              <div>
                <h2 className="text-2xl font-heading font-bold text-slate-900">{user?.name}</h2>
                <p className="text-slate-500 flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  {user?.email}
                </p>
                <div className="flex gap-2 mt-2">
                  <Badge className="badge-gold capitalize">{user?.role}</Badge>
                  {user?.is_alumni && (
                    <Badge className="badge-success">Alumni</Badge>
                  )}
                  {profileType && <ProfileBadge type={profileType} />}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Profile Type Selector */}
        <Card className="card-surface" data-testid="profile-type-card">
          <CardHeader>
            <CardTitle className="text-slate-900 text-lg">Select Your Profile Type</CardTitle>
            <p className="text-slate-500 text-sm">Choose the badge that best describes your relationship with Aspiritech.</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
              {PROFILE_TYPE_OPTIONS.map((opt) => {
                const Icon = opt.icon;
                const isSelected = profileType === opt.value;
                return (
                  <button
                    key={opt.value}
                    onClick={() => handleProfileTypeChange(opt.value)}
                    disabled={profileTypeLoading}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all text-left ${
                      isSelected
                        ? `${opt.bgColor} ${opt.textColor} border-current shadow-md scale-[1.02]`
                        : "border-slate-200 text-slate-500 hover:border-white/20 hover:text-slate-600"
                    }`}
                    data-testid={`select-type-${opt.value}`}
                  >
                    <Icon className="w-5 h-5 shrink-0" />
                    <span className="text-sm font-medium">{opt.label}</span>
                    {isSelected && (
                      <span className="ml-auto w-2 h-2 rounded-full bg-current animate-pulse" />
                    )}
                  </button>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Local Workforce Development Board */}
        <WorkforceBoardCard user={user} onZipSaved={checkAuth} />

        {/* Instructor-only: Zoom Personal Link */}
        <InstructorZoomCard />

        {/* Edit Form */}
        <Card className="card-surface">
          <CardHeader>
            <CardTitle className="text-slate-900 flex items-center gap-2">
              <User className="w-5 h-5 text-brand-navy" />
              Edit Profile
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Name */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-600">Display Name</Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="input-base"
                  data-testid="input-name"
                />
              </div>

              {/* Bio */}
              <div className="space-y-2">
                <Label htmlFor="bio" className="text-slate-600">Bio</Label>
                <Textarea
                  id="bio"
                  name="bio"
                  value={formData.bio}
                  onChange={handleChange}
                  placeholder="Tell us about yourself..."
                  className="input-base min-h-[120px]"
                  data-testid="input-bio"
                />
              </div>

              {/* Skills */}
              <div className="space-y-2">
                <Label className="text-slate-600">Skills</Label>
                <div className="flex gap-2">
                  <Input
                    value={newSkill}
                    onChange={(e) => setNewSkill(e.target.value)}
                    placeholder="Add a skill..."
                    className="input-base"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                    data-testid="input-new-skill"
                  />
                  <Button type="button" onClick={addSkill} className="btn-secondary">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {formData.skills.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {formData.skills.map((skill, index) => (
                      <span 
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-brand-gold/20 text-brand-navy rounded-full text-sm"
                      >
                        {skill}
                        <button type="button" onClick={() => removeSkill(skill)} className="hover:text-slate-900">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Interests */}
              <div className="space-y-2">
                <Label className="text-slate-600">Interests</Label>
                <div className="flex gap-2">
                  <Input
                    value={newInterest}
                    onChange={(e) => setNewInterest(e.target.value)}
                    placeholder="Add an interest..."
                    className="input-base"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addInterest())}
                    data-testid="input-new-interest"
                  />
                  <Button type="button" onClick={addInterest} className="btn-secondary">
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {formData.interests.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-3">
                    {formData.interests.map((interest, index) => (
                      <span 
                        key={index}
                        className="inline-flex items-center gap-1 px-3 py-1 bg-[#1E3A8A]/30 text-[#3B82F6] rounded-full text-sm"
                      >
                        {interest}
                        <button type="button" onClick={() => removeInterest(interest)} className="hover:text-slate-900">
                          <X className="w-3 h-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Social Links */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="linkedin_url" className="text-slate-600 flex items-center gap-2">
                    <Linkedin className="w-4 h-4" /> LinkedIn URL
                  </Label>
                  <Input
                    id="linkedin_url"
                    name="linkedin_url"
                    value={formData.linkedin_url}
                    onChange={handleChange}
                    placeholder="https://linkedin.com/in/..."
                    className="input-base"
                    data-testid="input-linkedin"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="portfolio_url" className="text-slate-600 flex items-center gap-2">
                    <Globe className="w-4 h-4" /> Portfolio URL
                  </Label>
                  <Input
                    id="portfolio_url"
                    name="portfolio_url"
                    value={formData.portfolio_url}
                    onChange={handleChange}
                    placeholder="https://..."
                    className="input-base"
                    data-testid="input-portfolio"
                  />
                </div>
              </div>

              {/* Submit */}
              <div className="flex justify-end pt-4">
                <Button 
                  type="submit" 
                  className="btn-primary flex items-center gap-2" 
                  disabled={loading}
                  data-testid="save-profile-btn"
                >
                  {loading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* Badges & Certifications */}
        <BadgesAndCerts userId={user?.user_id} isOwn={true} />

        {/* Two-factor authentication */}
        <MfaSettingsCard />
      </div>
    </PageLayout>
  );
};

export default Profile;
